import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthService } from "../services/authService";
import { AlertTriangleIcon, BookOpenIcon } from "./SvgIcons";
import LanguageSelector from "./LanguageSelector";
import { TranslationKey, useLanguage } from "../contexts/LanguageContext";
import { consumeReturnTarget } from "../routing/appRoutes";

interface OnboardingFlowProps {
  userProfile?: any; // Should have at least id, name, etc.
  onComplete: (userId: string, userProfile: any) => void;
}

const cefrLevels = [
  {
    level: "A1",
    proxy: {
      toefl: "0-31",
      ielts: "1.0-2.5",
      duolingo: "10-55",
    },
  },
  {
    level: "A2",
    proxy: {
      toefl: "32-42",
      ielts: "3.0-3.5",
      duolingo: "60-75",
    },
  },
  {
    level: "B1",
    proxy: {
      toefl: "43-71",
      ielts: "4.0-5.0",
      duolingo: "80-95",
    },
  },
  {
    level: "B2",
    proxy: {
      toefl: "72-94",
      ielts: "5.5-6.5",
      duolingo: "100-125",
    },
  },
  {
    level: "C1",
    proxy: {
      toefl: "95-113",
      ielts: "7.0-8.0",
      duolingo: "130-145",
    },
  },
  {
    level: "C2",
    proxy: {
      toefl: "114-120",
      ielts: "8.5-9.0",
      duolingo: "150-160",
    },
  },
];

const onboardingCompleteInterest = "general";

const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ userProfile: userProfileProp, onComplete }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [userProfile, setUserProfile] = useState<any>(userProfileProp || null);
  const [selectedLevel, setSelectedLevel] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (userProfileProp) {
      setUserProfile(userProfileProp);
      return;
    }

    // Try to load from localStorage
    const stored = localStorage.getItem("onboardingUserProfile");
    if (stored) {
      try {
        setUserProfile(JSON.parse(stored));
      } catch {
        localStorage.removeItem("onboardingUserProfile");
      }
    }
  }, [userProfileProp]);

  const handleFinishSetup = async () => {
    if (!userProfile) {
      setError(t("onboarding.errorNoProfile"));
      return;
    }

    if (!selectedLevel) {
      setError(t("onboarding.errorChooseLevel"));
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const updateResult = await AuthService.updateUserProfile(userProfile.id, {
        level: selectedLevel,
        interests: [onboardingCompleteInterest],
        target_tier: selectedLevel,
      });

      if (updateResult.error) {
        setError(t("onboarding.errorUpdate", { message: updateResult.error.message }));
        return;
      }

      const finalUserProfile = updateResult.data || {
        ...userProfile,
        level: selectedLevel,
        interests: [onboardingCompleteInterest],
        targetTier: selectedLevel,
      };
      
      // Clean up onboarding user profile from localStorage
      localStorage.removeItem("onboardingUserProfile");
      
      // Call onComplete to complete the login process
      onComplete(finalUserProfile.id, finalUserProfile);
      
      // Navigate to the main app after successful onboarding
      navigate(consumeReturnTarget(), { replace: true });
      
    } catch (err) {
      setError(t("onboarding.errorSetup", { message: (err as Error).message }));
    } finally {
      setIsLoading(false);
    }
  };

  const renderLevelSelection = () => (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <div className="max-w-5xl w-full bg-white rounded-2xl shadow-xl p-6 sm:p-8">
        <div className="mb-6 flex justify-end">
          <LanguageSelector />
        </div>
        <div className="text-center mb-8">
          <BookOpenIcon className="mx-auto mb-4 text-4xl text-sky-600" />
          <h1 className="text-3xl font-bold text-gray-800 mb-3">
            {t("onboarding.welcome", {
              name: userProfile?.name || t("onboarding.user"),
            })}
          </h1>
          <p className="mx-auto max-w-2xl text-gray-600">
            {t("onboarding.subtitle")}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-8">
          {cefrLevels.map((level) => {
            const isSelected = selectedLevel === level.level;

            return (
              <button
                key={level.level}
                onClick={() => setSelectedLevel(level.level)}
                className={`text-left rounded-lg border-2 p-5 transition-all duration-200 ${
                  isSelected
                    ? "border-sky-500 bg-sky-50 shadow-md"
                    : "border-gray-200 hover:border-sky-200 hover:bg-gray-50"
                }`}
              >
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      {level.level}
                    </div>
                    <div className="text-sm font-semibold text-sky-700">
                      {t(`level.${level.level}.name` as TranslationKey)}
                    </div>
                  </div>
                  <span
                    className={`h-5 w-5 rounded-full border-2 ${
                      isSelected
                        ? "border-sky-600 bg-sky-600"
                        : "border-gray-300 bg-white"
                    }`}
                    aria-hidden="true"
                  ></span>
                </div>
                <p className="mb-4 min-h-[60px] text-sm leading-5 text-gray-600">
                  {t(`level.${level.level}.description` as TranslationKey)}
                </p>
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="rounded-md bg-white px-2 py-2 ring-1 ring-gray-200">
                    <div className="font-semibold text-gray-500">TOEFL</div>
                    <div className="font-bold text-gray-800">{level.proxy.toefl}</div>
                  </div>
                  <div className="rounded-md bg-white px-2 py-2 ring-1 ring-gray-200">
                    <div className="font-semibold text-gray-500">IELTS</div>
                    <div className="font-bold text-gray-800">{level.proxy.ielts}</div>
                  </div>
                  <div className="rounded-md bg-white px-2 py-2 ring-1 ring-gray-200">
                    <div className="font-semibold text-gray-500">DET</div>
                    <div className="font-bold text-gray-800">
                      {level.proxy.duolingo}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-500">
            {t("onboarding.changeLater")}
          </p>
          <button
            onClick={handleFinishSetup}
            disabled={!selectedLevel || isLoading}
            className="px-8 py-3 bg-sky-600 text-white rounded-lg font-medium hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            {isLoading ? t("onboarding.settingUp") : t("onboarding.startReading")}
          </button>
        </div>
      </div>
    </div>
  );

  if (error && !userProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-pink-50 to-orange-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="text-red-500 mb-4">
            <AlertTriangleIcon className="mx-auto text-4xl" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {t("onboarding.setupError")}
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.href = "/"}
            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-200"
          >
            {t("onboarding.backToLogin")}
          </button>
        </div>
      </div>
    );
  }

  return renderLevelSelection();
};

export default OnboardingFlow;
